/**
 * Editorial highlights for the homepage spread. Static, build-time data —
 * matches the precedent of `data/site.ts`. When/if highlights move to the
 * CMS this becomes the adapter target, not the consumer change point.
 *
 * `getActiveHighlights()` is the single read path used by the homepage deck.
 * Keep the date gates accurate so an editor can leave expired entries in the
 * array indefinitely without anyone touching code. Order in the deck is
 * chronological by `publishedAt` (ascending), ties broken by id alphabetically.
 */

import type { Highlight } from '../types';

export const HIGHLIGHTS: Highlight[] = [
  {
    id: 'caominhada-2026-05',
    active: true,
    publishedAt: '2026-05-15',
    expiresAt: '2026-05-31',

    accent: 'terracotta',
    kind: 'evento',
    bookmarkDate: '30 mai',
    bookmarkKind: 'Cãominhada',
    bookmarkNick: '— Mia',

    ribbonLabel: 'Caderno · Mai 2026',
    ribbonNumber: 'N.º 47',
    pageNumber: 'pág. 47',
    chapterMark: 'Em destaque',
    signoff: 'com carinho, Equipa Patinhas ✿',
    doodle: '— corta esta página, leva-a contigo',

    title: { pre: 'Cã', accent: 'o', post: 'minhada' },
    undertitle: 'Parque *João de Deus*, Espinho · sábado, 30 de Maio',
    scribble: '— mais passos, mais amizade, mais felizes juntos',

    body: [
      {
        kind: 'paragraph',
        dropcap: true,
        text: 'A 30 de Maio, a Patinhas Sem Lar realiza a *Cãominhada* no Parque João de Deus, em Espinho. As atividades começam às 10h00; a caminhada parte às *15h30*.',
      },
      {
        kind: 'paragraph',
        text: 'A inscrição é de *quatro euros por patudo*. Faz-se por email para geral@patinhassemlar.org, com pagamento por MB Way para 915 372 325 ou 915 241 036 — basta colocar «*CÃOMINHADA*» na descrição.',
      },
      {
        kind: 'pull',
        text: '— mais passos, mais amizade, mais felizes juntos',
      },
      {
        kind: 'paragraph',
        text: 'Quem não puder vir mas quiser ajudar pode usar o mesmo MB Way para um *donativo direto*. As atualizações estão no Instagram e no Facebook da Patinhas Sem Lar.',
      },
    ],

    polaroid: {
      illustration: 'walking-pair',
      caption: '30 de Maio · 15h30',
      tapeColor: 'ochre',
      rotation: -3,
      stamp: { small: 'só', big: '4€', tiny: 'por patudo' },
    },

    pills: [
      { glyph: 'calendar', label: 'Data',    value: '30 de *Maio* · sábado' },
      { glyph: 'clock',    label: 'Horário', value: '10h *atividades* · 15h30 caminhada' },
      { glyph: 'pin',      label: 'Onde',    value: 'P. *João de Deus*, Espinho' },
    ],

    helpNote: {
      label: 'Como inscrever',
      title: { pre: 'Três ', accent: 'passos' },
      rows: [
        { k: '1. email',     v: '*geral*@patinhassemlar.org' },
        { k: '2. mb way',    v: '915 372 325 · 915 241 036' },
        { k: '3. descrição', v: '«CÃOMINHADA»' },
      ],
      handTag: '— ou aparece no parque a partir das 10h',
    },

    ctas: [
      { kind: 'primary',   label: 'Inscrever a matilha →', action: 'subscribe' },
      { kind: 'ghost',     label: 'Partilhar com amigos',  action: 'share' },
      { kind: 'scribbled', label: 'guardar para depois',   action: 'save' },
    ],

    event: {
      email: 'geral@patinhassemlar.org',
      emailSubject: 'Cãominhada · inscrever a matilha',
      emailBody:
        'Olá Patinhas,\n\nQueria inscrever a minha matilha na Cãominhada de 30 de Maio.\n\n' +
        'Nome: \nNº de patudos: \nContacto: \n\n' +
        'Faço o pagamento por MB Way para 915 372 325 ou 915 241 036 com a descrição «CÃOMINHADA».\n\n' +
        'Obrigado!\n',

      date: '2026-05-30',
      location: 'Parque João de Deus, Espinho',
      summary: 'Cãominhada — Patinhas Sem Lar',
      description:
        'Cãominhada da Patinhas Sem Lar no Parque João de Deus, Espinho. ' +
        'Atividades a partir das 10h00; caminhada às 15h30. ' +
        '4€ por patudo · MB Way 915 372 325 / 915 241 036 (descrição: CÃOMINHADA). ' +
        'Inscrições por geral@patinhassemlar.org.',

      shareUrl: 'https://www.instagram.com/p/DYHuXf1H-3_/',
      shareTitle: 'Cãominhada · 30 Maio · Espinho',
      shareText:
        'Vem caminhar connosco a 30 de Maio no Parque João de Deus, em Espinho. ' +
        'Mais passos, mais amizade, mais felizes juntos.',
    },
  },

  {
    id: 'recolha-realty-one-2026-05',
    active: true,
    publishedAt: '2026-05-14',
    expiresAt: '2026-05-30',

    accent: 'ochre',
    kind: 'recolha',
    bookmarkDate: '14–29 mai',
    bookmarkKind: '3 portas',
    bookmarkNick: '— em Espinho',

    ribbonLabel: 'Caderno · Mai 2026',
    ribbonNumber: 'N.º 48',
    pageNumber: 'pág. 48',
    chapterMark: 'Em destaque',
    signoff: 'com carinho, Equipa Patinhas ✿',
    doodle: '— passa onde te der jeito · todas as portas servem',

    title: { pre: 'Tr', accent: 'ê', post: 's portas' },
    undertitle: 'Quinze dias de *recolha* em Espinho · 14 a 29 de *Maio*',
    scribble: '— três comércios deram-nos a mão',

    body: [
      {
        kind: 'paragraph',
        dropcap: true,
        text: "A *Realty ONE Group Start*, o *Complexo de Ténis de Espinho* e o *19 Men's Hair Studio* uniram-se à nossa causa: durante quinze dias, três das suas portas são pontos de recolha para os bens que os patudos mais precisam.",
      },
      {
        kind: 'paragraph',
        text: 'Procuramos *ração de gato* (seca e húmida), sacos de lixo, rolo de cozinha, luvas descartáveis, detergente de roupa e de louça, toalhitas, vassouras e pás pequeninas. Cada pequena ajuda faz a diferença.',
      },
      {
        kind: 'pull',
        text: '— juntos, fazemos a diferença',
      },
      {
        kind: 'paragraph',
        text: 'Podes passar por *qualquer um dos três pontos* à hora que te der jeito — todos têm horários alargados. Tudo o que for entregue será encaminhado para o abrigo, *a quem não tem voz*.',
      },
    ],

    polaroid: {
      illustration: 'paws',
      caption: '— três portas, uma causa',
      tapeColor: 'ochre',
      rotation: -2,
      stamp: { small: 'recolha', big: '15', tiny: 'dias' },
    },

    pills: [
      { glyph: 'calendar', label: 'Janela', value: '14 a 29 de *Maio* · 15 dias' },
      { glyph: 'pin',      label: 'Pontos', value: '*3 lojas* em Espinho' },
      { glyph: 'paw',      label: 'Bens',   value: '*8 essenciais* · ver lista' },
    ],

    helpNote: {
      label: 'Onde entregar',
      title: { pre: 'Três ', accent: 'pontos' },
      rows: [
        { k: 'Realty ONE',    v: 'R. 15, *n.º 260* · seg–sex 10–13h + 14h30–19h' },
        { k: "19 Men's Hair", v: 'R. 15, *n.º 252* · ter–sex 10–19h · sáb 9–16h' },
        { k: 'Ténis Espinho', v: 'R. Porto, *413* · todos os dias · 7h–23h' },
      ],
      handTag: '— qualquer um serve, à hora que dê jeito',
    },

    ctas: [
      {
        kind: 'primary',
        label: 'Ver no Instagram →',
        href: 'https://www.instagram.com/p/DYZJTqVFHOy/',
      },
      { kind: 'ghost',     label: 'Partilhar a campanha',  action: 'share' },
      { kind: 'scribbled', label: 'guardar para depois',   action: 'save' },
    ],

    event: {
      email: 'geral@patinhassemlar.org',
      emailSubject: 'Recolha de bens · Realty ONE + parceiros',
      emailBody:
        'Olá Patinhas,\n\n' +
        "Queria entregar bens num dos pontos de recolha (Realty ONE Group Start / 19 Men's Hair Studio / Complexo de Ténis de Espinho).\n\n" +
        'Loja preferida: \nBens que vou levar: \nDia aproximado: \n\n' +
        'Obrigado!\n',
      date: '2026-05-14',
      location: 'Espinho · 3 pontos de recolha',
      summary: "Recolha Patinhas · Realty ONE + 19 Men's + Ténis Espinho",
      description:
        'Recolha de bens essenciais para a Patinhas Sem Lar, 14 a 29 de Maio. ' +
        "Pontos: Realty ONE Group Start (Rua 15, n.º 260), 19 Men's Hair Studio (Rua 15, n.º 252), Complexo de Ténis de Espinho (Rua do Porto, 413). " +
        'Aceitam ração de gato, sacos de lixo, rolo de cozinha, luvas, detergentes, toalhitas, vassouras e pás.',
      shareUrl: 'https://www.instagram.com/p/DYZJTqVFHOy/',
      shareTitle: 'Recolha em Espinho · 14–29 Mai · três portas',
      shareText:
        "A Patinhas Sem Lar e três comércios de Espinho (Realty ONE Group Start, 19 Men's Hair Studio, Complexo de Ténis de Espinho) abrem 3 pontos de recolha de bens essenciais entre 14 e 29 de Maio.",
    },
  },

  {
    id: 'recolha-pingo-doce-2026-05',
    active: true,
    publishedAt: '2026-05-15',
    expiresAt: '2026-06-01',

    accent: 'ochre',
    kind: 'recolha',
    bookmarkDate: '30+31 mai',
    bookmarkKind: 'Recolha',
    bookmarkNick: '— Pingo Doce',

    ribbonLabel: 'Caderno · Mai 2026',
    ribbonNumber: 'N.º 49',
    pageNumber: 'pág. 49',
    chapterMark: 'Em destaque',
    signoff: 'com carinho, Equipa Patinhas ✿',
    doodle: '— traz uma sacola, sai com o coração mais cheio',

    title: { pre: 'Rec', accent: 'o', post: 'lha' },
    undertitle: 'Dois dias no *Pingo Doce* de São Félix · 30 e 31 de *Maio*',
    scribble: '— precisamos de mãos junto ao carrinho',

    body: [
      {
        kind: 'paragraph',
        dropcap: true,
        text: 'O *Pingo Doce* de São Félix da Marinha abre-nos as portas para mais uma campanha de recolha de bens essenciais. Vamos estar à entrada da loja no sábado e no domingo, a acolher quem entra, a explicar a causa, e a guardar o que for sendo doado.',
      },
      {
        kind: 'paragraph',
        text: 'Precisamos de *dois voluntários por turno* — sem mãos suficientes, a campanha não acontece. Cada turno é umas horas; cada par de mãos faz a diferença para uns quantos patudos.',
      },
      {
        kind: 'pull',
        text: '— só conseguimos com a vossa ajuda',
      },
      {
        kind: 'paragraph',
        text: 'Se podes dar uma tarde ou uma manhã, envia-nos uma *mensagem privada* no Instagram para combinarmos o turno. Se só podes passar lá e deixar uma doação, também conta — vai *direto* ao abrigo na segunda-feira seguinte.',
      },
    ],

    polaroid: {
      illustration: 'cat-portrait',
      caption: '— SF Marinha · 30+31 mai',
      tapeColor: 'ochre',
      rotation: -3,
      stamp: { small: 'precisam', big: '2', tiny: 'por turno' },
    },

    pills: [
      { glyph: 'calendar', label: 'Quando',     value: '30 e 31 de *Maio* · sáb+dom' },
      { glyph: 'pin',      label: 'Onde',       value: '*Pingo Doce* SF Marinha' },
      { glyph: 'paw',      label: 'Precisamos', value: '*2 voluntários* por turno' },
    ],

    helpNote: {
      label: 'Como ajudar',
      title: { pre: 'Dois ', accent: 'passos' },
      rows: [
        { k: '1. mensagem', v: 'DM ao *@patinhassemlar*' },
        { k: '2. turno',    v: 'dia *30* ou dia *31* · combinamos a hora' },
      ],
      handTag: '— ou aparece lá e deixa uma doação',
    },

    ctas: [
      { kind: 'primary',   label: 'Voluntariar-me →',     action: 'subscribe' },
      { kind: 'ghost',     label: 'Partilhar com amigos', action: 'share' },
      { kind: 'scribbled', label: 'guardar para depois',  action: 'save' },
    ],

    event: {
      email: 'geral@patinhassemlar.org',
      emailSubject: 'Recolha Pingo Doce · voluntariar-me',
      emailBody:
        'Olá Patinhas,\n\n' +
        'Queria ajudar nos turnos da recolha no Pingo Doce de São Félix da Marinha (30 e 31 de Maio).\n\n' +
        'Nome: \nDia preferido: \nTurno (manhã / tarde): \nContacto: \n\n' +
        'Obrigado!\n',
      date: '2026-05-30',
      location: 'Pingo Doce, São Félix da Marinha',
      summary: 'Recolha Patinhas Sem Lar · Pingo Doce SF Marinha',
      description:
        'Campanha de recolha de bens essenciais para os patudos da Patinhas Sem Lar, no Pingo Doce de São Félix da Marinha, dias 30 e 31 de Maio. ' +
        'Precisamos de 2 voluntários por turno — DM ao @patinhassemlar para combinar.',
      shareUrl: 'https://www.instagram.com/p/DYSaumzuBvK/',
      shareTitle: 'Recolha Pingo Doce · 30+31 Mai · SF Marinha',
      shareText:
        'A Patinhas Sem Lar está no Pingo Doce de São Félix da Marinha, 30 e 31 de Maio. ' +
        'Precisam de 2 voluntários por turno e de doações de bens essenciais. DM ao @patinhassemlar.',
    },
  },

  {
    id: 'campanha-vaquinha-bebes-2026-06',
    // Retired 2026-10 — replaced by the PPL crowdfunding for the vet debt
    // (campanha-ppl-dividas-2026-10). Kept for the record.
    active: false,
    publishedAt: '2026-06-09',
    expiresAt: '2026-12-31', // ongoing — push out as the campaign continues

    accent: 'rust',
    kind: 'ninhada', // drives the rust bookmark color; visible label is bookmarkKind below
    bookmarkDate: 'em curso',
    bookmarkKind: 'Vaquinha',
    bookmarkNick: '— bebés',

    ribbonLabel: 'Caderno · Jun 2026',
    ribbonNumber: 'N.º 50',
    pageNumber: 'pág. 50',
    chapterMark: 'Em destaque',
    signoff: 'com carinho, Equipa Patinhas ✿',
    doodle: '— ajuda que cabe num só gesto',

    title: { pre: 'Rec', accent: 'é', post: 'm-resgatados' },
    undertitle: 'Uma *vaquinha* pelos que chegam mais pequeninos · contribui com o que puderes',
    scribble: '— os que chegam mais pequenos são os que mais precisam',

    body: [
      {
        kind: 'paragraph',
        dropcap: true,
        text: 'Há patudos que chegam até nós com poucos dias de vida — pequeninos demais para uma família, ainda. Antes de poderem ser adotados, precisam de *leite especial*, biberões de duas em duas horas, veterinário e um sítio quente para crescer.',
      },
      {
        kind: 'paragraph',
        text: 'Cada contributo, por mais pequeno, paga uma lata de leite, uma consulta, uma noite de calor — e dá-lhes tempo até estarem prontos.',
      },
      {
        kind: 'pull',
        text: '— cada euro chega-lhes antes de qualquer outra coisa',
      },
      {
        kind: 'paragraph',
        text: 'Podes contribuir com o *valor que quiseres*, quando quiseres, na página da campanha. E se agora não puder ser, *partilha* — às vezes o gesto que salva é o de quem espalha a palavra.',
      },
    ],

    polaroid: {
      illustration: 'kittens',
      caption: '— os recém-chegados',
      tapeColor: 'terracotta',
      rotation: -3,
      stamp: { small: 'cada', big: '€', tiny: 'conta' },
    },

    pills: [
      { glyph: 'heart', label: 'Por quem',  value: 'os *bebés* resgatados' },
      { glyph: 'price', label: 'Quanto',    value: 'o valor que *quiseres*' },
      { glyph: 'paw',   label: 'Onde vai',  value: '*leite*, veterinário e calor' },
    ],

    helpNote: {
      label: 'Como ajudar',
      title: { pre: 'Três ', accent: 'gestos' },
      rows: [
        { k: '1. abrir',     v: '*ppl.pt*/PatinhasSemLar' },
        { k: '2. escolher',  v: 'o *valor* que puderes dar' },
        { k: '3. partilhar', v: 'a campanha com *quem gosta deles*' },
      ],
      handTag: '— qualquer valor ajuda, qualquer partilha também',
    },

    ctas: [
      {
        kind: 'primary',
        label: 'Contribuir agora →',
        href: 'https://ppl.pt/PatinhasSemLar',
      },
      { kind: 'ghost', label: 'Partilhar a campanha', action: 'share' },
    ],

    event: {
      // No date — ongoing campaign. Only the share payload is used.
      shareUrl: 'https://ppl.pt/PatinhasSemLar',
      shareTitle: 'Vaquinha pelos bebés resgatados · Patinhas Sem Lar',
      shareText:
        'A Patinhas Sem Lar tem uma vaquinha contínua pelos patudos que chegam mais ' +
        'pequeninos — leite especial, veterinário e calor até estarem prontos para adoção. ' +
        'Contribui com o que puderes em ppl.pt/PatinhasSemLar.',
    },
  },
  {
    id: 'campanha-ppl-dividas-2026-10',
    active: true,
    publishedAt: '2026-10-08',
    expiresAt: '2026-11-26T18:00:00+00:00', // PPL closes at 18h on 26 Nov

    accent: 'terracotta',
    kind: 'campanha',
    bookmarkDate: 'até 26 nov',
    bookmarkKind: 'Crowdfund',
    bookmarkNick: '— clínicas',

    ribbonLabel: 'Caderno · Out 2026',
    ribbonNumber: 'N.º 51',
    pageNumber: 'pág. 51',
    chapterMark: 'Campanha em curso',
    signoff: 'com carinho, Equipa Patinhas ✿',
    doodle: '— os animais que salvámos não sabem o que é uma dívida',

    title: { pre: '400 v', accent: 'i', post: 'das' },
    undertitle: 'e *30 mil euros* em dívida às clínicas que as trataram · campanha até *26 de Novembro*',
    scribble: '— nunca dissemos «não» a um animal por ser caro tratá-lo',

    body: [
      {
        kind: 'paragraph',
        dropcap: true,
        text: 'Em 2025 recolhemos cerca de *400 animais*, esterilizámos 350 gatos de rua e cuidámos, todos os dias, de 110 cães no abrigo e 80 gatos no gatil — com duas funcionárias e muitos voluntários.',
      },
      {
        kind: 'paragraph',
        text: 'Gastámos mais de 203 mil euros, quase *120 mil só em veterinário*. Hoje devemos 30 mil euros às clínicas que trataram o Fredy, atropelado, a Mãe Tricolor e as quatro crias que encontrámos numa caixa, e tantos outros.',
      },
      {
        kind: 'pull',
        text: '— cada euro vai direto para pagar as clínicas',
      },
      {
        kind: 'paragraph',
        text: 'Sem pagar estas dívidas não conseguimos continuar a responder aos pedidos de ajuda que nos chegam todos os dias. Contribui na *PPL* com o que puderes, e se agora não der, *partilha*: vamos publicar o destino de cada euro.',
      },
    ],

    polaroid: {
      illustration: 'cone',
      caption: '— em recuperação, graças a vocês',
      tapeColor: 'terracotta',
      rotation: -3,
      stamp: { small: 'salvámos', big: '400', tiny: 'em 2025' },
    },

    pills: [
      { glyph: 'heart',    label: 'Para quê',   value: 'pagar as *clínicas* veterinárias' },
      { glyph: 'calendar', label: 'Até quando', value: '*26 de Novembro* · 18h' },
      { glyph: 'price',    label: 'Quanto',     value: 'o que puderes · *5€ já conta*' },
    ],

    // Not rendered while `campaign` is set; kept so the entry stays valid if
    // the campaign block is ever removed.
    helpNote: {
      label: 'Como ajudar',
      title: { pre: 'Dois ', accent: 'gestos' },
      rows: [
        { k: '1. contribuir', v: '*ppl.pt*/PatinhasSemLar26' },
        { k: '2. partilhar',  v: 'a campanha com *quem gosta deles*' },
      ],
    },

    // Snapshot from the PPL page — update `raised`, `backers` and
    // `updatedAt` whenever you touch this entry.
    campaign: {
      label: 'Conta corrente · clínicas',
      goal: 30000,
      raised: 1200,
      backers: 54,
      endsAt: '2026-11-26T18:00:00+00:00',
      updatedAt: '2026-10-11',
      tiers: [
        { amount: 5,  people: 6000 },
        { amount: 10, people: 3000 },
        { amount: 30, people: 1000 },
      ],
      handTag: '— o valor certo está sempre na PPL',
    },

    ctas: [
      {
        kind: 'primary',
        label: 'Contribuir na PPL →',
        href: 'https://ppl.pt/PatinhasSemLar26',
      },
      { kind: 'ghost', label: 'Partilhar a campanha', action: 'share' },
    ],

    event: {
      shareUrl: 'https://ppl.pt/PatinhasSemLar26',
      shareTitle: '400 vidas salvas, 30 mil euros em dívida · Patinhas Sem Lar',
      shareText:
        'Em 2025 a Patinhas Sem Lar salvou 400 animais em Espinho e ficou a dever 30 mil euros ' +
        'às clínicas veterinárias. Cada euro vai direto para essas dívidas. Contribui ou partilha:',
    },
  },
];

/**
 * All currently-active highlights, ordered chronologically by `publishedAt`
 * (ascending), with ties broken by `id` ascending so the order is stable
 * when two entries share a publish date.
 */
export function getActiveHighlights(now: Date = new Date()): Highlight[] {
  const nowMs = now.getTime();
  return HIGHLIGHTS.filter(
    (h) =>
      h.active &&
      new Date(h.publishedAt).getTime() <= nowMs &&
      new Date(h.expiresAt).getTime() >= nowMs,
  ).sort((a, b) => {
    const da = new Date(a.publishedAt).getTime();
    const db = new Date(b.publishedAt).getTime();
    if (da !== db) return da - db;
    return a.id.localeCompare(b.id);
  });
}

/**
 * Legacy single-highlight selector. Returns the first entry from
 * `getActiveHighlights()` — useful for any future caller (a /destaques/[id]
 * detail page, an embed) that wants one specific spread without the deck.
 */
export function getActiveHighlight(now: Date = new Date()): Highlight | null {
  return getActiveHighlights(now)[0] ?? null;
}
